import { ReactNode, useCallback, useState, useMemo } from 'react';
import type { SetOptional } from 'type-fest';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { useUserTimeZone } from '@cast/split-me/hooks/useUserTimeZone';
import { useIsKentRebalancingMode } from '@cast/split-me/features/cluster/rebalance';
import { notReachable } from '@cast/utils';

import { AutoOptimizationModal } from './AutoOptimizationModal';
import { AutoOptimizationScreenRouter } from './AutoOptimizationScreenRouter';
import {
  DEFAULT_REBALANCING_SCHEDULE_CRON,
  DEFAULT_REBALANCING_SCHEDULE_TIME_ZONE,
  KENT_REBALANCING_SCHEDULE_TIME_ZONE,
  LOADING_SCREENS,
  SCREEN_TO_STEP_INDEX,
  STEP_INDEX_TO_SCREEN,
} from '../_constants';
import { AutoOptimizationContext } from '../_context/AutoOptimizationContext';
import {
  INITIAL_STATE,
  useAutoPilotPersistedState,
} from '../_hooks/useAutoPilotPersistedState';
import { useCanUseOptimizationWizard } from '../_hooks/useCanUseOptimizationWizard';
import { useBinPackingFlow } from '../_hooks/useBinPackingFlow';
import { useRebalancingPlanFlow } from '../_hooks/useRebalancingPlanFlow';
import { useScheduleRebalancingFlow } from '../_hooks/useScheduleRebalancingFlow';
import { useWorkloadAutoscaler } from '../_hooks/useWorkloadAutoscaler';
import {
  ApplyState,
  AutoOptimizationScreen,
  AutoOptimizationWizardState,
  BinPackingFlowState,
  EvictorApplyState,
  RebalanceApplyState,
  RebalancingPlanFlowState,
  RebalancingScheduleFormState,
  ScheduleApplyState,
} from '../_types';
import {
  EnvironmentType,
  OptimizationGoal,
} from '../open-api-types/autopilot-types';

const isRebalancingPlanFlowInProgress = (
  state: RebalancingPlanFlowState
): boolean => {
  switch (state.type) {
    case 'nodes-eligibility-check':
    case 'generating-plan':
      return true;
    case 'idle':
    case 'nodes-eligibility-check-error':
    case 'no-eligible-nodes':
    case 'plan-generation-error':
    case 'plan-generated':
      return false;
    default:
      return notReachable(state);
  }
};

const isRebalancingPlanFlowFailed = (
  state: RebalancingPlanFlowState
): boolean => {
  switch (state.type) {
    case 'nodes-eligibility-check-error':
    case 'plan-generation-error':
      return true;
    case 'idle':
    case 'nodes-eligibility-check':
    case 'no-eligible-nodes':
    case 'generating-plan':
    case 'plan-generated':
      return false;
    default:
      return notReachable(state);
  }
};

const isBinPackingFlowInProgress = (
  binPackingFlowState: BinPackingFlowState
): boolean => {
  switch (binPackingFlowState.mode) {
    case 'autoscaler':
      return false;
    case 'kent': {
      const { kentBinPackingFlowState } = binPackingFlowState;
      switch (kentBinPackingFlowState.type) {
        case 'checking-crb-compatibility':
        case 'checking-crb-compatibility-error':
          return true;
        case 'checking-crb-compatibility-complete':
          return false;
        default:
          return notReachable(kentBinPackingFlowState);
      }
    }
    default:
      return notReachable(binPackingFlowState);
  }
};

const shouldApplyEvictorConfig = (
  binPackingFlowState: BinPackingFlowState
): boolean => {
  switch (binPackingFlowState.mode) {
    case 'autoscaler':
      return true;
    case 'kent': {
      const { kentBinPackingFlowState } = binPackingFlowState;
      switch (kentBinPackingFlowState.type) {
        case 'checking-crb-compatibility':
        case 'checking-crb-compatibility-error':
          return false;
        case 'checking-crb-compatibility-complete':
          return kentBinPackingFlowState.isCompatible;
        default:
          return notReachable(kentBinPackingFlowState);
      }
    }
    default:
      return notReachable(binPackingFlowState);
  }
};

const INITIAL_APPLY_STATE: ApplyState = {
  currentStep: 'create',
  error: null,
  createdPolicyIds: {},
  updatesCompleted: false,
  orderCompleted: false,
  rebalance: null,
  schedule: null,
  evictor: null,
  applyRetryAttempts: 0,
};

type AutoOptimizationProviderProps = {
  children: ReactNode;
  /** Called after the wizard modal is closed (X, Escape, or programmatic close). */
  onClosed?: () => void;
};

// todo:autopilot if something wen wrong and completed on the request, possible need extra handling
// todo:autopilot add event log on policy create/udate

export const AutoOptimizationProvider = ({
  children,
  onClosed,
}: AutoOptimizationProviderProps) => {
  const [openModal, setOpenModal] = useState(false);
  const { cluster } = useCluster();
  const currentOrganizationId = useCurrentOrganizationId();
  const [state, setState] = useAutoPilotPersistedState(cluster.id);
  const { canApplyOptimization } = useCanUseOptimizationWizard();

  const setCurrentScreen = useCallback(
    (s: AutoOptimizationScreen) =>
      setState((prev) => ({ ...prev, currentScreen: s })),
    [setState]
  );

  const [rebalanceImmediately, setRebalanceImmediately] = useState(false);

  const isKentRebalancingMode = useIsKentRebalancingMode();

  const [
    rebalancingScheduleUserSelection,
    setRebalancingScheduleUserSelection,
  ] = useState<SetOptional<RebalancingScheduleFormState, 'timeZone'>>({
    enabled: false,
    cron: DEFAULT_REBALANCING_SCHEDULE_CRON,
    timeZone: undefined,
  });

  const userTimeZone = useUserTimeZone();
  // Derived rather than stored. "undefined" means the user has made no selection.
  // This lets the default resolve naturally when the async timezone data arrives
  // and means reset can simply clear the user's preference. Kent is UTC-only and
  // overrides any user choice. The effective value is never empty, so on apply we can
  // always provide timezone
  const effectiveScheduleTimeZone = isKentRebalancingMode
    ? KENT_REBALANCING_SCHEDULE_TIME_ZONE
    : rebalancingScheduleUserSelection.timeZone ||
      userTimeZone?.name ||
      DEFAULT_REBALANCING_SCHEDULE_TIME_ZONE;

  const rebalancingScheduleFormState = useMemo<RebalancingScheduleFormState>(
    () => ({
      ...rebalancingScheduleUserSelection,
      timeZone: effectiveScheduleTimeZone,
    }),
    [rebalancingScheduleUserSelection, effectiveScheduleTimeZone]
  );

  const onToggleScheduleOption = useCallback(() => {
    setRebalancingScheduleUserSelection((previousSelections) => ({
      ...previousSelections,
      enabled: !previousSelections.enabled,
    }));
  }, []);

  const onUpdateScheduleForm = useCallback(
    ({
      cron,
      timeZone,
    }: Pick<RebalancingScheduleFormState, 'cron' | 'timeZone'>) => {
      setRebalancingScheduleUserSelection((previousSelections) => ({
        ...previousSelections,
        cron,
        timeZone,
      }));
    },
    []
  );

  const setRecommendations = useCallback(
    (recommendations: AutoOptimizationWizardState['recommendations']) => {
      setState((prev) => ({ ...prev, recommendations }));
    },
    [setState]
  );

  const updateRebalanceState = useCallback(
    (update: Partial<RebalanceApplyState>) => {
      setState((prev) => ({
        ...prev,
        applyState: prev.applyState
          ? {
              ...prev.applyState,
              rebalance: prev.applyState.rebalance
                ? { ...prev.applyState.rebalance, ...update }
                : { status: 'loading', error: null, ...update },
            }
          : prev.applyState,
      }));
    },
    [setState]
  );

  const updateScheduleState = useCallback(
    (update: Partial<ScheduleApplyState>) => {
      setState((prev) => ({
        ...prev,
        applyState: prev.applyState
          ? {
              ...prev.applyState,
              schedule: prev.applyState.schedule
                ? { ...prev.applyState.schedule, ...update }
                : { status: 'loading', error: null, ...update },
            }
          : prev.applyState,
      }));
    },
    [setState]
  );

  const updateEvictorState = useCallback(
    (update: Partial<EvictorApplyState>) => {
      setState((prev) => ({
        ...prev,
        applyState: prev.applyState
          ? {
              ...prev.applyState,
              evictor: prev.applyState.evictor
                ? { ...prev.applyState.evictor, ...update }
                : { status: 'loading', error: null, ...update },
            }
          : prev.applyState,
      }));
    },
    [setState]
  );

  const {
    state: rebalancingPlanFlowState,
    generateRebalancingPlan,
    resetState: resetRebalancingPlanFlowState,
    executeRebalancingPlan,
  } = useRebalancingPlanFlow({
    clusterId: cluster.id,
  });

  const { state: binPackingFlowState, configureBinPacking } = useBinPackingFlow(
    {
      cluster,
      organizationId: currentOrganizationId,
    }
  );

  const { scheduleRebalancing } = useScheduleRebalancingFlow();

  const isLoadingScreen = useMemo(
    () =>
      LOADING_SCREENS.includes(state.currentScreen) ||
      state.recommendations.isLoading ||
      isRebalancingPlanFlowInProgress(rebalancingPlanFlowState),
    [
      state.currentScreen,
      state.recommendations.isLoading,
      rebalancingPlanFlowState,
    ]
  );

  const backButtonStatus = useMemo((): 'hidden' | 'disabled' | 'default' => {
    if (
      state.currentScreen === AutoOptimizationScreen.Features ||
      state.currentScreen === AutoOptimizationScreen.Applying ||
      state.currentScreen === AutoOptimizationScreen.Completed
    ) {
      return 'hidden';
    }
    if (
      isLoadingScreen ||
      state.recommendations.error ||
      isRebalancingPlanFlowFailed(rebalancingPlanFlowState)
    ) {
      return 'disabled';
    }
    return 'default';
  }, [
    state.currentScreen,
    state.recommendations.error,
    rebalancingPlanFlowState,
    isLoadingScreen,
  ]);

  const nextButtonStatus = useMemo((): 'hidden' | 'disabled' | 'default' => {
    if (
      state.currentScreen === AutoOptimizationScreen.Applying ||
      state.currentScreen === AutoOptimizationScreen.Completed
    ) {
      return 'hidden';
    }
    if (state.currentScreen === AutoOptimizationScreen.Features) {
      const hasFeature =
        state.features.nodeAutoscaling || state.features.workloadAutoscaling;
      return hasFeature ? 'default' : 'disabled';
    }
    if (state.currentScreen === AutoOptimizationScreen.Priority) {
      const canContinue =
        state.preferences.priority !== null &&
        state.preferences.clusterUsage !== null;
      return canContinue ? 'default' : 'disabled';
    }
    if (state.currentScreen === AutoOptimizationScreen.Recommendations) {
      const workloadLoading =
        state.features.workloadAutoscaling && state.recommendations.isLoading;
      const workloadError =
        state.features.workloadAutoscaling &&
        Boolean(state.recommendations.error);
      const nodeLoading =
        state.features.nodeAutoscaling &&
        isRebalancingPlanFlowInProgress(rebalancingPlanFlowState);
      const nodeError =
        state.features.nodeAutoscaling &&
        isRebalancingPlanFlowFailed(rebalancingPlanFlowState);
      const nothingToApply =
        !state.features.workloadAutoscaling &&
        !rebalanceImmediately &&
        !rebalancingScheduleFormState.enabled &&
        !shouldApplyEvictorConfig(binPackingFlowState);

      if (
        workloadLoading ||
        workloadError ||
        nodeLoading ||
        nodeError ||
        isBinPackingFlowInProgress(binPackingFlowState) ||
        nothingToApply ||
        !canApplyOptimization
      ) {
        return 'disabled';
      }
      return 'default';
    }
    return 'default';
  }, [
    state.currentScreen,
    state.preferences.priority,
    state.preferences.clusterUsage,
    state.features.nodeAutoscaling,
    state.features.workloadAutoscaling,
    state.recommendations.isLoading,
    state.recommendations.error,
    rebalancingPlanFlowState,
    rebalanceImmediately,
    rebalancingScheduleFormState.enabled,
    binPackingFlowState,
    canApplyOptimization,
  ]);

  const handleResetRebalancingPlanState = useCallback(() => {
    resetRebalancingPlanFlowState();
    setRebalanceImmediately(false);
  }, [resetRebalancingPlanFlowState, setRebalanceImmediately]);

  const updateApplyState = useCallback(
    (update: Partial<ApplyState>) => {
      setState((prev) => ({
        ...prev,
        applyState: prev.applyState
          ? { ...prev.applyState, ...update }
          : { ...INITIAL_APPLY_STATE, ...update },
      }));
    },
    [setState]
  );

  const {
    generate: generateWorkload,
    cancel: cancelWorkload,
    onUpdatePolicyManagementOption,
    runWorkloadPolicies,
  } = useWorkloadAutoscaler({
    clusterId: cluster.id,
    clusterName: cluster.name ?? '',
    preferences: state.preferences,
    excludedNamespaces: state.excludedNamespaces,
    recommendations: state.recommendations,
    setRecommendations,
    updateApplyState,
    onGenerateSuccess: () => {
      setState((prev) => ({
        ...prev,
        applyState: prev.applyState
          ? { ...prev.applyState, applyRetryAttempts: 0 }
          : prev.applyState,
      }));
    },
  });

  const onSelectPriority = useCallback(
    (priority: OptimizationGoal) => {
      setState((prev) => ({
        ...prev,
        preferences: { ...prev.preferences, priority },
      }));
    },
    [setState]
  );

  const onSelectEnvironment = useCallback(
    (clusterUsage: EnvironmentType) => {
      setState((prev) => ({
        ...prev,
        preferences: { ...prev.preferences, clusterUsage },
      }));
    },
    [setState]
  );

  const onToggleFeature = useCallback(
    (feature: 'nodeAutoscaling' | 'workloadAutoscaling') => {
      setState((prev) => ({
        ...prev,
        features: {
          ...prev.features,
          [feature]: !prev.features[feature],
        },
      }));
    },
    [setState]
  );

  const onToggleRebalanceOption = useCallback(() => {
    setRebalanceImmediately((previousValue) => !previousValue);
  }, []);

  const onSetExcludedNamespaces = useCallback(
    (excludedNamespaces: string[]) => {
      setState((prev) => ({ ...prev, excludedNamespaces }));
    },
    [setState]
  );

  const onGenerate = useCallback(async () => {
    setCurrentScreen(AutoOptimizationScreen.Recommendations);

    if (state.features.workloadAutoscaling) {
      setRecommendations({ ...INITIAL_STATE.recommendations, isLoading: true });
      generateWorkload();
    } else {
      setRecommendations(INITIAL_STATE.recommendations);
    }

    if (state.features.nodeAutoscaling) {
      setRebalanceImmediately(false);
      generateRebalancingPlan(state.preferences.priority!);
    } else {
      handleResetRebalancingPlanState();
      setRebalancingScheduleUserSelection((previousSelections) => ({
        ...previousSelections,
        enabled: false,
      }));
    }
  }, [
    state.features,
    state.preferences.priority,
    setRecommendations,
    generateWorkload,
    generateRebalancingPlan,
    handleResetRebalancingPlanState,
    setCurrentScreen,
  ]);

  const onRetryNodeRebalancePlan = useCallback(() => {
    generateRebalancingPlan(state.preferences.priority!);
  }, [state.preferences.priority, generateRebalancingPlan]);

  const shouldScheduleRebalancing =
    state.features.nodeAutoscaling && rebalancingScheduleFormState.enabled;

  const handleScheduleRebalancing = useCallback(async () => {
    if (!state.preferences.priority) {
      return;
    }

    updateScheduleState({ status: 'loading', error: null });
    const succeeded = await scheduleRebalancing({
      cron: rebalancingScheduleFormState.cron,
      timeZone: rebalancingScheduleFormState.timeZone,
      optimizationGoal: state.preferences.priority,
      cluster: cluster,
      organizationId: currentOrganizationId,
    });
    if (succeeded) {
      updateScheduleState({ status: 'succeeded', error: null });
      return true;
    }
    updateScheduleState({
      status: 'error',
      error: 'Failed to apply rebalancing schedule.',
    });
    return false;
  }, [
    scheduleRebalancing,
    updateScheduleState,
    rebalancingScheduleFormState.cron,
    rebalancingScheduleFormState.timeZone,
    state.preferences.priority,
    cluster,
    currentOrganizationId,
  ]);

  const shouldExecuteRebalancingPlan =
    state.features.nodeAutoscaling &&
    rebalancingPlanFlowState.type === 'plan-generated' &&
    rebalancingPlanFlowState.hasPositiveSavings &&
    rebalanceImmediately;

  const handleExecuteRebalancingPlan = useCallback(async () => {
    updateRebalanceState({ status: 'loading', error: null });
    const succeeded = await executeRebalancingPlan();
    if (succeeded) {
      updateRebalanceState({ status: 'succeeded', error: null });
      return true;
    }
    updateRebalanceState({
      status: 'error',
      error: 'Failed to execute rebalancing plan.',
    });
    return false;
  }, [executeRebalancingPlan, updateRebalanceState]);

  const handleConfigureBinPacking = useCallback(async () => {
    if (!state.preferences.priority) {
      return false;
    }

    updateEvictorState({ status: 'loading', error: null });
    const succeeded = await configureBinPacking({
      optimizationGoal: state.preferences.priority,
      excludedNamespaces: state.excludedNamespaces,
    });

    if (succeeded) {
      updateEvictorState({ status: 'succeeded', error: null });
      return true;
    }

    updateEvictorState({
      status: 'error',
      error: 'Failed to configure bin-packing.',
    });
    return false;
  }, [
    configureBinPacking,
    updateEvictorState,
    state.preferences.priority,
    state.excludedNamespaces,
  ]);

  const runApply = useCallback(
    async (applyState: ApplyState) => {
      const [
        workloadSucceeded,
        rebalancingSucceeded,
        scheduleSucceeded,
        evictorSucceeded,
      ] = await Promise.all([
        state.features.workloadAutoscaling
          ? runWorkloadPolicies(applyState)
          : Promise.resolve(true),
        shouldExecuteRebalancingPlan &&
        applyState.rebalance?.status !== 'succeeded'
          ? handleExecuteRebalancingPlan()
          : Promise.resolve(true),
        shouldScheduleRebalancing && applyState.schedule?.status !== 'succeeded'
          ? handleScheduleRebalancing()
          : Promise.resolve(true),
        shouldApplyEvictorConfig(binPackingFlowState) &&
        applyState.evictor?.status !== 'succeeded'
          ? handleConfigureBinPacking()
          : Promise.resolve(true),
      ]);
      if (
        workloadSucceeded &&
        rebalancingSucceeded &&
        scheduleSucceeded &&
        evictorSucceeded
      ) {
        setCurrentScreen(AutoOptimizationScreen.Completed);
      }
    },
    [
      state.features.workloadAutoscaling,
      shouldExecuteRebalancingPlan,
      shouldScheduleRebalancing,
      runWorkloadPolicies,
      binPackingFlowState,
      handleExecuteRebalancingPlan,
      handleScheduleRebalancing,
      handleConfigureBinPacking,
      setCurrentScreen,
    ]
  );

  const onApply = useCallback(() => {
    const initialApplyState = { ...INITIAL_APPLY_STATE };
    setState((prev) => ({
      ...prev,
      applyState: initialApplyState,
    }));
    setCurrentScreen(AutoOptimizationScreen.Applying);
    runApply(initialApplyState);
  }, [runApply, setState, setCurrentScreen]);

  const onRetryApply = useCallback(() => {
    let shouldReset = false;
    let nextApplyState: ApplyState | null = null;

    setState((prev) => {
      const currentAttempts = (prev.applyState?.applyRetryAttempts ?? 0) + 1;

      if (currentAttempts >= 3) {
        shouldReset = true;
        return INITIAL_STATE;
      }

      const base = prev.applyState
        ? {
            ...prev.applyState,
            error: null,
            evictor:
              prev.applyState.evictor?.status === 'succeeded'
                ? prev.applyState.evictor
                : null,
            schedule:
              prev.applyState.schedule?.status === 'succeeded'
                ? prev.applyState.schedule
                : null,
            rebalance:
              prev.applyState.rebalance?.status === 'succeeded'
                ? prev.applyState.rebalance
                : null,
          }
        : { ...INITIAL_APPLY_STATE };
      const next: ApplyState = {
        ...base,
        applyRetryAttempts: currentAttempts,
      };
      nextApplyState = next;
      return { ...prev, applyState: next };
    });

    if (shouldReset) {
      setCurrentScreen(AutoOptimizationScreen.Features);
      return;
    }

    if (nextApplyState) {
      runApply(nextApplyState);
    }
  }, [runApply, setState, setCurrentScreen]);

  const navigateTo = useCallback(
    (s: AutoOptimizationScreen) => setCurrentScreen(s),
    [setCurrentScreen]
  );

  const onBack = useCallback(() => {
    switch (state.currentScreen) {
      case AutoOptimizationScreen.Priority:
        setCurrentScreen(AutoOptimizationScreen.Features);
        break;
      case AutoOptimizationScreen.Exclusions:
        setCurrentScreen(AutoOptimizationScreen.Priority);
        break;
      case AutoOptimizationScreen.Recommendations:
        cancelWorkload();
        handleResetRebalancingPlanState();
        setState((prev) => ({
          ...prev,
          recommendations: INITIAL_STATE.recommendations,
        }));
        setCurrentScreen(AutoOptimizationScreen.Exclusions);
        break;
      case AutoOptimizationScreen.Applying:
      case AutoOptimizationScreen.Completed:
        setCurrentScreen(AutoOptimizationScreen.Recommendations);
        break;
      default:
        break;
    }
  }, [
    state.currentScreen,
    cancelWorkload,
    handleResetRebalancingPlanState,
    setState,
    setCurrentScreen,
  ]);

  const onReset = useCallback(() => {
    handleResetRebalancingPlanState();
    setRebalancingScheduleUserSelection((previousSelections) => ({
      ...previousSelections,
      enabled: false,
    }));
    setState(INITIAL_STATE);
    setOpenModal(false);
  }, [handleResetRebalancingPlanState, setState]);

  const onNext = useCallback(() => {
    switch (state.currentScreen) {
      case AutoOptimizationScreen.Features:
        setCurrentScreen(AutoOptimizationScreen.Priority);
        break;
      case AutoOptimizationScreen.Priority:
        setCurrentScreen(AutoOptimizationScreen.Exclusions);
        break;
      case AutoOptimizationScreen.Exclusions:
        onGenerate();
        break;
      case AutoOptimizationScreen.Recommendations:
        onApply();
        break;
      default:
        break;
    }
  }, [state.currentScreen, setCurrentScreen, onGenerate, onApply]);

  const onStepClick = useCallback(
    (stepIndex: number) => {
      if (isLoadingScreen || state.recommendations.isLoading) {
        return;
      }

      const currentIndex = SCREEN_TO_STEP_INDEX[state.currentScreen];
      if (stepIndex >= currentIndex) return;

      const targetScreen = STEP_INDEX_TO_SCREEN[stepIndex];
      if (!targetScreen) return;

      const clearingRecommendations =
        stepIndex <
        SCREEN_TO_STEP_INDEX[AutoOptimizationScreen.Recommendations];

      if (clearingRecommendations) {
        cancelWorkload();
        handleResetRebalancingPlanState();
        setRebalancingScheduleUserSelection((previousSelections) => ({
          ...previousSelections,
          enabled: false,
        }));
      }

      setState((prev) => {
        const clearingApply =
          stepIndex < SCREEN_TO_STEP_INDEX[AutoOptimizationScreen.Applying];
        return {
          ...prev,
          recommendations: clearingRecommendations
            ? INITIAL_STATE.recommendations
            : prev.recommendations,
          applyState: clearingApply
            ? null
            : prev.applyState
            ? { ...prev.applyState, applyRetryAttempts: 0 }
            : prev.applyState,
        };
      });
      setCurrentScreen(targetScreen);
    },
    [
      isLoadingScreen,
      state.currentScreen,
      state.recommendations.isLoading,
      cancelWorkload,
      handleResetRebalancingPlanState,
      setState,
      setCurrentScreen,
    ]
  );

  const onClose = useCallback(() => {
    setOpenModal(false);
    onClosed?.();
  }, [onClosed]);

  const startOpsPilot = useCallback(() => {
    // Cancel any in-flight generation before opening so stale results don't
    // land after the wizard resets.
    cancelWorkload();
    handleResetRebalancingPlanState();
    setRebalancingScheduleUserSelection({
      enabled: false,
      cron: DEFAULT_REBALANCING_SCHEDULE_CRON,
      timeZone: undefined,
    });
    setState(INITIAL_STATE);
    setOpenModal(true);
  }, [cancelWorkload, handleResetRebalancingPlanState, setState]);

  return (
    <AutoOptimizationContext.Provider
      value={{
        currentScreen: state.currentScreen,
        isLoadingScreen,
        state,
        rebalancingPlanFlowState,
        binPackingFlowState,
        rebalancingScheduleFormState,
        rebalanceImmediately,
        startOpsPilot,
        onClose,
        onSelectPriority,
        onSelectEnvironment,
        onToggleFeature,
        onToggleRebalanceOption,
        onToggleScheduleOption,
        onUpdateScheduleForm,
        onGenerate,
        onRetryApply,
        onRetryNodeRebalancePlan,
        onSetExcludedNamespaces,
        navigateTo,
        onBack,
        backButtonStatus,
        onNext,
        nextButtonStatus,
        applyState: state.applyState,
        onStepClick,
        onReset,
        onUpdatePolicyManagementOption,
      }}
    >
      {children}
      <AutoOptimizationModal open={openModal}>
        <AutoOptimizationScreenRouter />
      </AutoOptimizationModal>
    </AutoOptimizationContext.Provider>
  );
};
